// ============================================================================
// WorkbenchDashboard.jsx  (v3 — dense two-column layout)
//
// Left:  Engagement context + unified findings/actions feed
// Right: Stats, report preparation, next steps
// ============================================================================

import React, { useMemo, useState } from 'react';
import {
  ArrowRight,
  CheckSquare,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  Info,
  Lightbulb,
  Users,
} from 'lucide-react';
import { statusTone, focusLabel } from '@process-advisor/process-miner/consultant/workbenchShared';
import * as S from '@process-advisor/process-miner/consultant/workbenchStyles';

const navigateToTab = (tabId) => {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent('metricus:consultant-tab-change', { detail: { tabId } }));
};

// ── Tiny components ─────────────────────────────────────────────────────────

const Pill = ({ children, tone = 'neutral' }) => (
  <span style={S.statusPillStyle(tone)}>{children}</span>
);

const Link = ({ children, onClick }) => (
  <button
    onClick={onClick}
    style={{
      background: 'none', border: 'none', padding: 0, color: S.C.B400,
      fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: S.FONT,
      display: 'inline-flex', alignItems: 'center', gap: 4,
    }}
  >
    {children}
  </button>
);

// ── Stat cell ───────────────────────────────────────────────────────────────

const STAT_TONES = {
  blue: S.C.B400, orange: S.C.Y400, green: S.C.G400, purple: S.C.P500,
};

const Stat = ({ label, value, tone = 'blue', onClick }) => (
  <div onClick={onClick} style={{ display: 'flex', alignItems: 'baseline', gap: 6, cursor: 'pointer', padding: '6px 0' }}>
    <span style={{ fontSize: 20, fontWeight: 800, color: STAT_TONES[tone] || S.C.B400, lineHeight: 1 }}>{value}</span>
    <span style={{ fontSize: 12, fontWeight: 600, color: S.C.N300 }}>{label}</span>
  </div>
);

// ── Activity feed item ──────────────────────────────────────────────────────

const FeedItem = ({ type, title, subtitle, severity, status, onClick }) => {
  const isFinding = type === 'finding';
  return (
    <div
      onClick={onClick}
      style={{
        display: 'flex', alignItems: 'center', gap: 10, padding: '7px 0',
        borderBottom: `1px solid ${S.C.N30}`, cursor: 'pointer',
      }}
    >
      <div style={{
        width: 28, height: 28, borderRadius: 7, flexShrink: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: isFinding ? '#FFF4E5' : '#E8F5E9',
      }}>
        {isFinding
          ? <Lightbulb size={15} color="#FF8B00" />
          : <CheckSquare size={15} color={S.C.G400} />
        }
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize: 13, fontWeight: 600, color: S.C.N800,
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        }}>{title}</div>
        <div style={{
          fontSize: 11, color: S.C.N300, marginTop: 1,
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        }}>
          <span style={{
            fontWeight: 700, color: isFinding ? '#FF8B00' : S.C.G400,
            marginRight: 5,
          }}>{isFinding ? 'Finding' : 'Action'}</span>
          {subtitle}
        </div>
      </div>
      <Pill tone="neutral">{severity}</Pill>
      <Pill tone={statusTone(status)}>{status}</Pill>
    </div>
  );
};

// ── Main component ──────────────────────────────────────────────────────────

const WorkbenchDashboard = ({ workspace = {} }) => {
  const findings     = workspace.findings || [];
  const actions      = workspace.actions  || [];

  const needsReview   = findings.filter(f => f.status === 'Needs Review').length;
  const validated     = findings.filter(f => ['Validated', 'Converted to Action'].includes(f.status)).length;
  const openActions   = actions.filter(a => !['Implemented', 'Rejected'].includes(a.status)).length;
  const hasBrief      = Boolean(workspace.objective || workspace.scope || workspace.businessArea);

  // Unified feed: findings + actions, most recent first
  const feed = useMemo(() => {
    const items = [
      ...findings.map(f => ({
        type: 'finding', id: f.id, title: f.title || 'Untitled finding',
        subtitle: focusLabel(f) || f.area,
        severity: f.severity, status: f.status,
        time: new Date(f.updatedAt || 0).getTime(),
        tab: 'workbenchFindings',
      })),
      ...actions.map(a => {
        const linked = findings.find(f => f.id === a.findingId);
        return {
          type: 'action', id: a.id, title: a.title || 'Untitled action',
          subtitle: linked ? `From: ${linked.title}` : a.actionType,
          severity: a.priority, status: a.status,
          time: new Date(a.updatedAt || 0).getTime(),
          tab: 'workbenchActions',
        };
      }),
    ];
    return items.sort((a, b) => b.time - a.time).slice(0, 8);
  }, [findings, actions]);

  // Next steps
  const nextSteps = useMemo(() => {
    const n = [];
    if (!hasBrief) n.push({ text: 'Set up the engagement brief', tab: 'workbenchBrief' });
    if (findings.length === 0) n.push({ text: 'Save findings from Advisor, Evidence, or Explorer', tab: null });
    if (needsReview > 0) n.push({ text: `Review ${needsReview} finding${needsReview > 1 ? 's' : ''}`, tab: 'workbenchFindings' });
    if (validated > 0 && openActions === 0) n.push({ text: `Convert ${validated} validated finding${validated > 1 ? 's' : ''} to actions`, tab: 'workbenchFindings' });
    if (findings.length > 0 || actions.length > 0) n.push({ text: 'Generate the assessment report when the analysis is ready', tab: 'consultingReport' });
    return n;
  }, [hasBrief, findings.length, actions.length, needsReview, validated, openActions]);

  // Explainer banner — collapsed after first dismiss via localStorage
  const [explainerOpen, setExplainerOpen] = useState(() => {
    try { return window.localStorage.getItem('metricus.workbench.explainerDismissed') !== 'true'; } catch (_) { return true; }
  });
  const dismissExplainer = () => {
    setExplainerOpen(false);
    try { window.localStorage.setItem('metricus.workbench.explainerDismissed', 'true'); } catch (_) {}
  };
  const showExplainer = () => {
    setExplainerOpen(true);
    try { window.localStorage.removeItem('metricus.workbench.explainerDismissed'); } catch (_) {}
  };

  return (
    <div style={{ ...S.ROOT, padding: '10px 16px' }}>
      <div style={S.SHELL}>

        {/* ── Explainer banner ──────────────────────────────────────── */}
        {explainerOpen ? (
          <div style={{
            background: S.C.N0, border: `1px solid ${S.C.N40}`, borderRadius: 10,
            padding: '10px 14px', marginBottom: 10,
          }}>
            <div
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}
              onClick={dismissExplainer}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                <Info size={15} color={S.C.B400} />
                <span style={{ fontSize: 13, fontWeight: 700, color: S.C.N800 }}>About the Assessment</span>
              </div>
              <ChevronUp size={16} color={S.C.N300} />
            </div>
            <div style={{ fontSize: 12, color: S.C.N400, lineHeight: 1.55, marginTop: 6 }}>
              Assessment is the consulting layer. Capture evidence-backed findings from the Advisor, Evidence, and Explorer tabs, validate what matters, then convert the important items into agreed actions. Keep the engagement brief up to date so the analysis stays anchored to real objectives, then generate the final client deliverable from the Report tab.
            </div>
            <div style={{ display: 'flex', gap: 16, marginTop: 8 }}>
              <div style={{ fontSize: 12, color: S.C.N400 }}><strong style={{ color: S.C.B400 }}>1.</strong> Capture findings from evidence</div>
              <div style={{ fontSize: 12, color: S.C.N400 }}><strong style={{ color: S.C.B400 }}>2.</strong> Validate and prioritise</div>
              <div style={{ fontSize: 12, color: S.C.N400 }}><strong style={{ color: S.C.B400 }}>3.</strong> Convert to agreed actions</div>
            </div>
          </div>
        ) : (
          <div
            style={{
              display: 'flex', alignItems: 'center', gap: 7, padding: '6px 14px',
              marginBottom: 10, cursor: 'pointer', borderRadius: 8,
              color: S.C.N300, fontSize: 12,
            }}
            onClick={showExplainer}
          >
            <Info size={14} color={S.C.N50} />
            <span>About the Assessment</span>
            <ChevronDown size={14} color={S.C.N50} />
          </div>
        )}

        {/* ── Two-column layout ─────────────────────────────────────── */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: 14, alignItems: 'start' }}>

        {/* ════════════ LEFT COLUMN ════════════ */}
        <div>

          {/* ── Engagement context ──────────────────────────────────── */}
          <div style={{
            background: S.C.N0, border: `1px solid ${S.C.N40}`, borderRadius: 10,
            padding: '10px 14px', marginBottom: 10,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: hasBrief ? 6 : 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                <Users size={15} color={S.C.B400} />
                <span style={{ fontSize: 13, fontWeight: 700, color: S.C.N800 }}>Engagement</span>
              </div>
              <Link onClick={() => navigateToTab('workbenchBrief')}>
                {hasBrief ? 'Edit' : 'Set up'} <ExternalLink size={12} />
              </Link>
            </div>
            {hasBrief ? (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2px 16px', fontSize: 12 }}>
                {workspace.objective && (
                  <div style={{ gridColumn: '1 / -1', display: 'flex', gap: 6, padding: '3px 0' }}>
                    <span style={{ color: S.C.N300, fontWeight: 700, minWidth: 65 }}>Objective</span>
                    <span style={{ color: S.C.N800 }}>{workspace.objective}</span>
                  </div>
                )}
                {workspace.scope && (
                  <div style={{ gridColumn: '1 / -1', display: 'flex', gap: 6, padding: '3px 0' }}>
                    <span style={{ color: S.C.N300, fontWeight: 700, minWidth: 65 }}>Scope</span>
                    <span style={{ color: S.C.N800 }}>{workspace.scope}</span>
                  </div>
                )}
                {workspace.businessArea && (
                  <div style={{ display: 'flex', gap: 6, padding: '3px 0' }}>
                    <span style={{ color: S.C.N300, fontWeight: 700 }}>Team</span>
                    <span style={{ color: S.C.N800 }}>{workspace.businessArea}</span>
                  </div>
                )}
                {workspace.processOwner && (
                  <div style={{ display: 'flex', gap: 6, padding: '3px 0' }}>
                    <span style={{ color: S.C.N300, fontWeight: 700 }}>Owner</span>
                    <span style={{ color: S.C.N800 }}>{workspace.processOwner}</span>
                  </div>
                )}
              </div>
            ) : (
              <div style={{ fontSize: 12, color: S.C.N300, padding: '4px 0' }}>
                No engagement brief yet — define the objective and scope to anchor the analysis.
              </div>
            )}
          </div>

          {/* ── Activity feed ───────────────────────────────────────── */}
          <div style={{
            background: S.C.N0, border: `1px solid ${S.C.N40}`, borderRadius: 10,
            padding: '10px 14px',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: S.C.N800 }}>Activity</span>
                <span style={{
                  background: S.C.B50, color: S.C.B400, fontSize: 11, fontWeight: 800,
                  padding: '1px 7px', borderRadius: 999,
                }}>{findings.length + actions.length}</span>
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                {findings.length > 0 && <Link onClick={() => navigateToTab('workbenchFindings')}>Findings <ArrowRight size={12} /></Link>}
                {actions.length > 0 && <Link onClick={() => navigateToTab('workbenchActions')}>Actions <ArrowRight size={12} /></Link>}
              </div>
            </div>

            {feed.length === 0 ? (
              <div style={{ fontSize: 12, color: S.C.N300, padding: '12px 0' }}>
                No findings or actions yet. Use <strong>Save Finding</strong> from Advisor, Evidence, or Explorer to start building the assessment.
              </div>
            ) : (
              <div>
                {feed.map(item => (
                  <FeedItem
                    key={item.id}
                    type={item.type}
                    title={item.title}
                    subtitle={item.subtitle}
                    severity={item.severity}
                    status={item.status}
                    onClick={() => navigateToTab(item.tab)}
                  />
                ))}
              </div>
            )}
          </div>
        </div>

        {/* ════════════ RIGHT COLUMN ════════════ */}
        <div>

          {/* ── Stats ───────────────────────────────────────────────── */}
          <div style={{
            background: S.C.N0, border: `1px solid ${S.C.N40}`, borderRadius: 10,
            padding: '8px 14px', marginBottom: 10,
          }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 12px' }}>
              <Stat label="Findings"     value={findings.length} tone="blue"   onClick={() => navigateToTab('workbenchFindings')} />
              <Stat label="Needs review" value={needsReview}     tone="orange" onClick={() => navigateToTab('workbenchFindings')} />
              <Stat label="Open actions" value={openActions}      tone="green"  onClick={() => navigateToTab('workbenchActions')} />
            </div>
          </div>

          {/* ── Report preparation ───────────────────────────────── */}
          <div style={{
            background: S.C.N0, border: `1px solid ${S.C.N40}`, borderRadius: 10,
            padding: '10px 14px', marginBottom: 10,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: S.C.N800 }}>Report material</span>
              <Link onClick={() => navigateToTab('consultingReport')}>Open Report <ArrowRight size={12} /></Link>
            </div>
            <div style={{ fontSize: 12, color: S.C.N400, lineHeight: 1.55 }}>
              Use Assessment to curate the items that should appear in the final report. Save the important evidence-backed findings, convert agreed items into actions, then generate the client-ready report from the Report tab.
            </div>
          </div>

          {/* ── Next steps ──────────────────────────────────────────── */}
          {nextSteps.length > 0 && (
            <div style={{
              background: 'linear-gradient(135deg, #f0f4ff 0%, #e8eeff 100%)',
              border: `1px solid ${S.C.B75}`, borderRadius: 10,
              padding: '10px 14px',
            }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: S.C.B400, marginBottom: 6 }}>Next steps</div>
              {nextSteps.map(({ text, tab }, i) => (
                <div key={i} style={{
                  display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0',
                  fontSize: 12, color: S.C.N400,
                }}>
                  <ArrowRight size={12} color={S.C.B400} style={{ flexShrink: 0 }} />
                  <span style={{ flex: 1 }}>{text}</span>
                  {tab && <Link onClick={() => navigateToTab(tab)}>Go</Link>}
                </div>
              ))}
            </div>
          )}

        </div>
      </div>
      </div>
    </div>
  );
};

export default WorkbenchDashboard;
